/**
 * Per-page SEO tags for the IP Intel Report landing page: meta description,
 * canonical link and the Open Graph basics, set while the page is mounted.
 *
 * The app is a single index.html, so these are written into <head> on mount
 * and put back exactly as they were on unmount (a tag we created is removed,
 * a tag that already existed gets its old value back) — the next route never
 * inherits this page's description or canonical. No dependency: plain DOM.
 * Sitemap and robots.txt already list /ip-intel-report; nothing to do there.
 */

import { useEffect } from 'react';

export interface PageMeta {
  title: string;
  description: string;
  canonical: string;
  type?: string;
  image?: string;
}

type Undo = () => void;

/** Set one attribute on the tag matched by `selector` (creating it if needed). */
function upsert(selector: string, create: () => HTMLElement, attr: string, value: string): Undo {
  let el = document.head.querySelector<HTMLElement>(selector);
  if (!el) {
    el = create();
    el.setAttribute(attr, value);
    document.head.appendChild(el);
    const created = el;
    return () => { created.remove(); };
  }
  const had = el.hasAttribute(attr);
  const old = el.getAttribute(attr);
  el.setAttribute(attr, value);
  const existing = el;
  return () => {
    if (had && old !== null) existing.setAttribute(attr, old);
    else existing.removeAttribute(attr);
  };
}

const metaName = (name: string, content: string): Undo =>
  upsert(`meta[name="${name}"]`, () => { const m = document.createElement('meta'); m.setAttribute('name', name); return m; }, 'content', content);

const metaProp = (prop: string, content: string): Undo =>
  upsert(`meta[property="${prop}"]`, () => { const m = document.createElement('meta'); m.setAttribute('property', prop); return m; }, 'content', content);

const linkRel = (rel: string, href: string): Undo =>
  upsert(`link[rel="${rel}"]`, () => { const l = document.createElement('link'); l.setAttribute('rel', rel); return l; }, 'href', href);

export function usePageMeta(meta: PageMeta): void {
  const { title, description, canonical, type = 'website', image } = meta;
  useEffect(() => {
    const undo: Undo[] = [];
    try {
      undo.push(metaName('description', description));
      undo.push(linkRel('canonical', canonical));
      undo.push(metaProp('og:title', title));
      undo.push(metaProp('og:description', description));
      undo.push(metaProp('og:url', canonical));
      undo.push(metaProp('og:type', type));
      if (image) undo.push(metaProp('og:image', image));
    } catch { /* a head we cannot write is not worth breaking the page */ }
    return () => { undo.reverse().forEach(u => { try { u(); } catch { /* ignore */ } }); };
  }, [title, description, canonical, type, image]);
}
